/**
 * loomSplit — P17-UAT-14A REV-B
 * =============================
 * Authoritative Work-Order resolution for split-head Loom Production shards.
 *
 * Capacity Planning is the WO-allocation authority. Each split-head plan
 * already carries its own `work_order_id` + `source_wo_no`. The old
 * `hydrateSplitRow` heuristic re-matched the WO by (source_order_id + item_id)
 * against the `workOrders` list, which — because sibling shards share the same
 * SO/ESO, the list is newest-first, and a stale header `item_id` can point at
 * the wrong product — collapsed both shards onto a single WO.
 *
 * Resolution priority (never let a heuristic override plan authority):
 *   1. plan.work_order_id   → find the exact WO by id.
 *   2. plan.source_wo_no    → authoritative displayed WO number.
 *   3. legacy heuristic     → ONLY when the plan lacks authoritative identity.
 *
 * The plan allocation stays authoritative even when a production order carries
 * stale header metadata. Conflicts are surfaced (never silently remapped).
 *
 * @returns {{workOrderId:string, woNo:string, woDoc:(object|null), conflict:(string|null)}}
 *   conflict ∈ null | "WO_NOT_IN_LIST" | "WO_NUMBER_MISMATCH"
 */
export function resolveShardWO(plan, workOrders) {
  const wos = Array.isArray(workOrders) ? workOrders : [];
  const planWoId = (plan && plan.work_order_id) || "";
  const planWoNo = (plan && plan.source_wo_no) || "";

  // Priority 1 · authoritative WO id from the Capacity Plan allocation.
  if (planWoId) {
    const byId = wos.find((w) => w.id === planWoId) || null;
    const woNo = planWoNo || (byId && (byId.po_no || byId.wo_no)) || "";
    let conflict = null;
    if (!byId) {
      // The authoritative id isn't in the loaded list — keep plan authority,
      // surface a data warning rather than remapping to some other WO.
      conflict = "WO_NOT_IN_LIST";
    } else if (
      planWoNo &&
      (byId.po_no || byId.wo_no) &&
      planWoNo !== byId.po_no &&
      planWoNo !== byId.wo_no
    ) {
      conflict = "WO_NUMBER_MISMATCH";
    }
    return { workOrderId: planWoId, woNo, woDoc: byId, conflict };
  }

  // Priority 2 · authoritative WO number from the allocation.
  if (planWoNo) {
    const byNo = wos.find((w) => w.po_no === planWoNo || w.wo_no === planWoNo) || null;
    return { workOrderId: (byNo && byNo.id) || "", woNo: planWoNo, woDoc: byNo, conflict: null };
  }

  // Priority 3 · LEGACY heuristic — only when the plan has no WO identity.
  const heur =
    wos.find(
      (w) =>
        w.source_order_id === plan.order_id &&
        (w.item_id === plan.item_id ||
          (w.items || []).some((li) => li.item_id === plan.item_id))
    ) ||
    wos.find((w) => w.item_id === plan.item_id) ||
    null;
  return {
    workOrderId: (heur && heur.id) || "",
    woNo: (heur && (heur.po_no || heur.wo_no)) || "",
    woDoc: heur,
    conflict: null,
  };
}

/**
 * P17-UAT-15 REV-C1 · Loom Production fail-closed identity guard.
 * A Capacity-Plan-derived Loom entry MUST carry the plan's authoritative
 * production identity. The payload's Work Order MUST equal
 * `plan.work_order_id`, and (when the plan carries one) the product MUST equal
 * `plan.product_code`. On conflict the caller BLOCKS the save — it must never
 * silently remap to a sibling WO/product from the same SO/ESO.
 *
 * @returns {null | "WO_MISMATCH" | "PRODUCT_MISMATCH"}
 *   null when the payload is consistent with the plan (or no plan authority).
 */
export function loomIdentityConflict(plan, payload) {
  if (!plan || !payload) return null;
  const planWo = plan.work_order_id || "";
  if (planWo && (payload.work_order_id || "") !== planWo) return "WO_MISMATCH";
  const planPc = plan.product_code || "";
  if (planPc && payload.product_code && payload.product_code !== planPc) return "PRODUCT_MISMATCH";
  return null;
}

/**
 * P17-UAT-14A REV-B2 · Loom register lineage — authoritative persisted-only.
 * Resolve the SO/ESO document number for a persisted production entry from its
 * own `order_id` + `order_kind` (never inferred from product / never heuristic).
 * Returns "—" when no authoritative reference resolves.
 */
export function loomOrderNo(entry, opts) {
  const orders = (opts && opts.orders) || [];
  const exportOrders = (opts && opts.exportOrders) || [];
  if (!entry) return "—";
  if (entry.source_order_no) return entry.source_order_no;
  if (entry.linked_source_no) return entry.linked_source_no;
  const oid = entry.order_id || "";
  if (!oid) return "—";
  const kind = (entry.order_kind || "").toUpperCase();
  const primary = kind === "ESO" ? exportOrders : orders;
  const secondary = kind === "ESO" ? orders : exportOrders;
  const hit = primary.find((o) => o.id === oid) || secondary.find((o) => o.id === oid) || null;
  return (hit && (hit.order_no || hit.eso_no || hit.so_no)) || "—";
}

/**
 * P17-UAT-14A REV-B2 · Loom register WO — the entry's OWN persisted
 * `work_order_id` (shard-specific for split-head). NO heuristic item matching,
 * so split shards keep distinct WOs and historical defective rows reflect their
 * actual persisted value. "—" when no authoritative WO reference exists.
 */
export function loomWoNo(entry, workOrders) {
  if (!entry) return "—";
  if (entry.work_order_no) return entry.work_order_no;
  if (entry.wo_no) return entry.wo_no;
  const wid = entry.work_order_id || "";
  if (!wid) return "—";
  const wo = (workOrders || []).find((w) => w.id === wid) || null;
  return (wo && (wo.po_no || wo.wo_no)) || "—";
}
